import React from "react";
import { Sprout, Compass, Scale, Wrench } from "lucide-react";

const IMG = "https://images.pexels.com/photos/414837/pexels-photo-414837.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=900";

const VALUES = [
  {
    icon: Compass,
    title: "Independence",
    body: "We work for outcomes, not for fees. Our recommendations stand on their own — including when they cost us an engagement.",
  },
  {
    icon: Sprout,
    title: "Environmental discipline",
    body: "Sustainability is a constraint, not a label. We design to measurable thresholds — emissions, water, waste, biodiversity.",
  },
  {
    icon: Scale,
    title: "Commercial honesty",
    body: "If a project doesn't pencil, we say so. We back only what can be built, operated, and financed at scale.",
  },
  {
    icon: Wrench,
    title: "Execution bias",
    body: "We prefer working models to immaculate slides. Answers live in the field, not the appendix.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-20">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B] mb-6">
          About Saafi Industries
        </p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter font-medium max-w-5xl">
          A firm built for the <em className="not-italic text-[#0F7A3E]">second</em> half of the transition.
        </h1>
      </section>

      {/* Story */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-28 md:pb-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-start">
          <div className="md:col-span-5">
            <div className="relative overflow-hidden aspect-[3/4] w-full">
              <img
                src={IMG}
                alt="Wind turbines at sunset — clean energy infrastructure"
                className="absolute inset-0 w-full h-full object-cover saafi-img-reveal"
              />
            </div>
            <p className="mt-4 text-[11px] uppercase tracking-[0.25em] text-[#79836B]">
              — Clean energy infrastructure, on the ground
            </p>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B] mb-4">
              Who We Are
            </p>
            <h2 className="font-display text-3xl md:text-4xl leading-snug tracking-tight font-medium mb-8">
              A consulting and investment firm at the intersection of
              environment, energy and industry.
            </h2>
            <p className="text-lg leading-relaxed text-[#575B57]">
              Saafi Industries is forward-looking by design. We partner with
              companies, investors and institutions to design, build and scale
              solutions that are both commercially viable and environmentally
              responsible.
            </p>
            <p className="mt-6 text-lg leading-relaxed text-[#575B57]">
              With deep experience across renewable energy, waste-to-value
              ecosystems, and industrial operations, we bring not just
              advisory — but execution-backed consulting that delivers
              measurable outcomes.
            </p>

            <div className="mt-12 grid grid-cols-2 gap-8">
              <Stat k="Mandate" v="Commercial viability × environmental responsibility" />
              <Stat k="Operating model" v="Advisory, capital, execution — under one roof" />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#F3F3E8]/60 border-y border-[#E4E5DF]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="mb-14">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B] mb-4">
              What we hold
            </p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight font-medium max-w-3xl">
              Four principles that govern every engagement.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-l border-[#E4E5DF]">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  data-testid={`value-card-${v.title.toLowerCase()}`}
                  className="p-10 md:p-12 border-b border-r border-[#E4E5DF] bg-white"
                >
                  <Icon className="w-7 h-7 text-[#0F7A3E] mb-8" strokeWidth={1.25} />
                  <p className="font-display text-2xl tracking-tight">{v.title}</p>
                  <p className="mt-4 text-[15px] leading-relaxed text-[#575B57]">
                    {v.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="border-t border-[#1A1C1A] pt-5">
      <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B] mb-2">{k}</p>
      <p className="font-display text-lg tracking-tight text-[#1A1C1A] leading-snug">{v}</p>
    </div>
  );
}
